// Cronômetros flutuantes: o do Ciclo (assina o timerStore de lib/timer) e o do Modo Prova
// (assina o examStore de lib/examTimer). Quem faz o tempo andar no Ciclo é o CicloAtivo,
// mantido montado pelo AppMain mesmo em outras abas; na prova, o próprio store.
import { useState, useEffect, useRef } from "react";
import { loadLS, saveLS } from "../lib/storage";
import { timerStore, useTimerStore } from "../lib/timer";
import { examToggle, examReset, requestExamOpen, useExamStore, examStore } from "../lib/examTimer";
import { appConfirm } from "../lib/dialog";
import { useTheme, useIsMobile } from "../lib/theme";
import { ContestEmblem } from "./ui";
import { NavIcon } from "./icons";

const fmtT = sec => { const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), x = sec % 60; const p2 = n => String(n).padStart(2, "0"); return h > 0 ? `${h}:${p2(m)}:${p2(x)}` : `${p2(m)}:${p2(x)}`; };

// Tamanhos (blocos maiores que antes, para ler de relance)
const EMBLEM = 42, BTN = 48, TIME_FS = "1.7rem", LABEL_FS = "0.72rem";
// Posição inicial: no celular fica acima da barra de abas; no computador, no canto inferior direito.
const BASE_BOTTOM_MOBILE = "calc(5.4rem + env(safe-area-inset-bottom))";
const BASE_BOTTOM_DESKTOP = "1.5rem";

const PlayIcon  = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>;
const PauseIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>;

// Caixa arrastável (posição guardada em posKey). Toque simples chama onOpen.
function FloatBox({ posKey, label, onOpen, highlight, watch, stack = 0, children }) {
  const t = useTheme();
  const isMob = useIsMobile();
  const boxRef = useRef(null);
  const drag = useRef(null);
  const [pos, setPos] = useState(() => loadLS(posKey, null));   // { x, y } em px, ou null = canto padrão
  const clamp = (x, y) => {
    const el = boxRef.current, w = el ? el.offsetWidth : 250, h = el ? el.offsetHeight : 78;
    return { x: Math.min(Math.max(6, x), Math.max(6, window.innerWidth - w - 6)), y: Math.min(Math.max(6, y), Math.max(6, window.innerHeight - h - 6)) };
  };
  useEffect(() => {
    const onResize = () => setPos(p => (p ? clamp(p.x, p.y) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // O bloco muda de largura (ex.: aparece o ✕): mantém dentro da tela
  useEffect(() => {
    if (!pos || !boxRef.current) return;
    const c = clamp(pos.x, pos.y);
    if (Math.abs(c.x - pos.x) > 0.5 || Math.abs(c.y - pos.y) > 0.5) setPos(c);
  }, [watch, pos]); // eslint-disable-line react-hooks/exhaustive-deps
  const down = e => {
    if (e.target.closest("[data-nodrag]")) return;
    const r = boxRef.current.getBoundingClientRect();
    drag.current = { sx: e.clientX, sy: e.clientY, ox: r.left, oy: r.top, moved: false };
    try { boxRef.current.setPointerCapture(e.pointerId); } catch {}
  };
  const move = e => {
    const d = drag.current; if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < 6) return;
    d.moved = true;
    setPos(clamp(d.ox + dx, d.oy + dy));
  };
  const up = () => {
    const d = drag.current; drag.current = null;
    if (!d) return;
    if (d.moved) { const r = boxRef.current.getBoundingClientRect(); saveLS(posKey, { x: r.left, y: r.top }); }
    else onOpen && onOpen();
  };
  const baseBottom = isMob ? BASE_BOTTOM_MOBILE : BASE_BOTTOM_DESKTOP;
  const bottom = stack ? `calc(${baseBottom} + ${stack * 5.4}rem)` : baseBottom;
  const place = pos ? { left: pos.x, top: pos.y } : { right: isMob ? 14 : 24, bottom };
  return (
    <div ref={boxRef} className="g17-float-timer" role="group" aria-label={label}
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => { drag.current = null; }} onDragStart={e => e.preventDefault()}
      style={{ position:"fixed", ...place, zIndex:90, display:"flex", alignItems:"center", gap:"0.7rem", padding:"0.6rem 0.7rem 0.6rem 0.8rem",
        background:t.bgCard, border:`1.5px solid ${highlight || t.border}`, borderRadius:20, cursor:"grab", touchAction:"none", userSelect:"none", WebkitUserSelect:"none",
        boxShadow: highlight ? `0 12px 34px rgba(0,0,0,0.55), 0 0 0 4px ${highlight}33` : "0 12px 34px rgba(0,0,0,0.55)", maxWidth:"calc(100vw - 12px)" }}>
      {children}
    </div>
  );
}

// ── Cronômetro flutuante do Ciclo ────────────────────────────────────────────────
// O store do cronômetro e os controles de tela de bloqueio vivem em ./lib/timer
// (importados no topo). O CicloAtivo publica o estado; o bloco flutuante só lê.
export function FloatingTimer({ onOpen }) {
  const t = useTheme();
  const st = useTimerStore();
  if (!st || !st.started) return null;
  return (
    <FloatBox posKey="sf_floatPos2" label="Cronômetro de estudo" onOpen={onOpen} highlight={st.running ? t.ac : null} watch={!!st.running}>
      <ContestEmblem id={st.contest} size={EMBLEM} />
      <div style={{ minWidth:0 }}>
        <div className="tac-num" style={{ fontSize:TIME_FS, fontWeight:800, color:t.text, lineHeight:1.1, letterSpacing:"0.02em", fontVariantNumeric:"tabular-nums" }}>{st.over ? "+" : ""}{fmtT(st.tl)}</div>
        <div style={{ fontSize:LABEL_FS, color:t.textDim, maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{st.running ? st.subjectName : `${st.subjectName} · pausado`}</div>
      </div>
      <button data-nodrag onClick={e => { e.stopPropagation(); timerStore.toggle && timerStore.toggle(); }}
        aria-label={st.running ? "Pausar cronômetro" : "Continuar cronômetro"} title={st.running ? "Pausar" : "Continuar"}
        style={{ width:BTN, height:BTN, borderRadius:"50%", border:"none", cursor:"pointer", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center",
          background: st.running ? "#ef4444" : st.color || t.ac, color:"#fff" }}>
        {st.running ? <PauseIcon /> : <PlayIcon />}
      </button>
      {!st.running && (
        <button data-nodrag onClick={e => { e.stopPropagation(); timerStore.dismiss && timerStore.dismiss(); }} aria-label="Fechar" title="Fechar"
          style={{ background:"transparent", border:"none", color:t.textFaint, cursor:"pointer", fontSize:"1rem", padding:"0.3rem", flexShrink:0 }}>✕</button>
      )}
    </FloatBox>
  );
}

// ── Cronômetro flutuante do Modo Prova ───────────────────────────────────────────
// Aparece quando a prova está em andamento (ou encerrada e ainda sem registro) e o painel
// do Modo Prova não está na tela. Toque no bloco: volta para o Modo Prova.
export function ExamFloatingTimer({ onOpen, stacked }) {
  const t = useTheme();
  const ex = useExamStore();
  const study = useTimerStore();
  if ((!ex.running && !ex.finished) || examStore.viewing) return null;
  const danger = ex.running && !ex.paused && ex.remaining <= 300;
  const warn   = ex.running && !ex.paused && ex.remaining > 300 && ex.remaining <= 600;
  const color  = ex.finished ? "#4ade80" : danger ? "#f87171" : warn ? "#fbbf24" : t.ac;
  const open = () => { requestExamOpen(); onOpen && onOpen(); };
  const discard = async () => {
    if (await appConfirm("O resultado desta prova ainda não foi registrado.", { title:"Descartar a prova?", confirmText:"Descartar", danger:true })) examReset();
  };
  return (
    <FloatBox posKey="sf_floatPosExam2" label="Cronômetro do Modo Prova" onOpen={open} highlight={color} watch={`${ex.finished}${ex.paused}`}
      stack={stacked && study && study.started ? 1 : 0}>
      <ContestEmblem id={ex.contest} size={EMBLEM} fallback={<span style={{ color:t.ac, display:"flex" }}><NavIcon id="b_primeiros_passos" size={EMBLEM * 0.62} /></span>} />
      <div style={{ minWidth:0 }}>
        {ex.finished
          ? <div style={{ fontSize:"1.15rem", fontWeight:800, color, lineHeight:1.15 }}>Prova encerrada</div>
          : <div className="tac-num" style={{ fontSize:TIME_FS, fontWeight:800, color, lineHeight:1.1, letterSpacing:"0.02em", fontVariantNumeric:"tabular-nums" }}>{fmtT(ex.remaining)}</div>}
        <div style={{ fontSize:LABEL_FS, color:t.textDim, maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {ex.finished ? "Toque para registrar" : ex.paused ? "Modo Prova · pausado" : "Modo Prova"}
        </div>
      </div>
      {!ex.finished && (
        <button data-nodrag onClick={e => { e.stopPropagation(); examToggle(); }}
          aria-label={ex.paused ? "Retomar prova" : "Pausar prova"} title={ex.paused ? "Retomar" : "Pausar"}
          style={{ width:BTN, height:BTN, borderRadius:"50%", border:"none", cursor:"pointer", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center",
            background: ex.paused ? t.ac : "#ef4444", color:"#fff" }}>
          {ex.paused ? <PlayIcon /> : <PauseIcon />}
        </button>
      )}
      {ex.finished && (
        <button data-nodrag onClick={e => { e.stopPropagation(); discard(); }} aria-label="Descartar" title="Descartar"
          style={{ background:"transparent", border:"none", color:t.textFaint, cursor:"pointer", fontSize:"1rem", padding:"0.3rem", flexShrink:0 }}>✕</button>
      )}
    </FloatBox>
  );
}
